import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Table, Button, Space, message, Image, Tag, Modal, Alert } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {  
  DeleteOutlined, 
  ArrowLeftOutlined, 
  UploadOutlined, 
  ReloadOutlined, 
  WarningOutlined, 
  PictureOutlined 
} from '@ant-design/icons';

// 导入产品图片相关 API 和类型
import { getImages, deleteImage } from '@/api/productImages';
import { getProduct } from '@/api/products';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { getCurrentDeveloperId } from '@/stores/authStore';
import type { ProductImage } from '@/types/productImage';

export const ProductImageList = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const developerId = getCurrentDeveloperId();
  
  // const [previewVisible, setPreviewVisible] = useState(false);
  // const [activePreviewId, setActivePreviewId] = useState<string>('');
  
  // 分页状态
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // 查询产品信息
  const productQuery = useQuery({
    queryKey: ['product', developerId, productId],
    queryFn: () => {
      if (!productId) throw new Error('产品 ID 不能为空');
      return getProduct(productId);
    },
    enabled: !!productId && !!developerId,
    staleTime: 30000,
  });

  // 查询产品图片列表
  const imageQuery = useQuery({
    queryKey: ['product-images', developerId, productId],
    queryFn: async (): Promise<ProductImage[]> => {
      if (!productId) throw new Error('产品 ID 不能为空');
      const response = await getImages(productId);
      return response.images;
    },
    enabled: !!productId && !!developerId,
    staleTime: 0,
    gcTime: 0,
  });

  // 按创建时间倒序排序（最新在前）
  const images: ProductImage[] = useMemo(() => {
    if (!imageQuery.data) return [];
    return [...imageQuery.data].sort((a, b) => {
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [imageQuery.data]);

  const product = productQuery.data;
  const loading = productQuery.isLoading || imageQuery.isLoading;
  const isRefreshing = imageQuery.isFetching && !imageQuery.isLoading;
  const error = productQuery.error || imageQuery.error;

  // 删除图片 Mutation
  const deleteMutation = useMutation({
    mutationFn: (filename: string) => {
      if (!productId) throw new Error('产品 ID 不能为空');
      return deleteImage(productId, filename);
    },
    onSuccess: () => {
      message.success('图片删除成功');
      queryClient.invalidateQueries({ queryKey: ['product-images', developerId, productId] });
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : '删除失败，请检查权限或网络。';
      message.error(errorMessage);
    },
  });

  // 刷新列表
  const handleRefresh = async () => {
    message.loading('正在刷新图片列表...', 0);
    await imageQuery.refetch();
    message.destroy();
    message.success('图片列表已刷新');
  };

  // 返回产品详情
  const handleBack = () => navigate(`/products/${productId}`);
  
  // 跳转到上传页面
  const handleUpload = () => navigate(`/product-images/upload?productId=${productId}`);

  // 删除确认
  const handleDelete = (filename: string) => {
    Modal.confirm({
      title: '确认删除图片',
      icon: <WarningOutlined className="text-orange-500" />,
      okType: 'danger',
      okText: '确认删除',
      cancelText: '取消',
      content: (
        <div className="space-y-2">
          <p>确定要删除图片 <span className="font-mono text-red-500">{filename}</span> 吗？</p>
          <p className="text-red-500 font-medium">此操作不可恢复，请谨慎操作！</p>
        </div>
      ),
      onOk: () => deleteMutation.mutate(filename),
    });
  };

  // ✅ 移除：图片预览相关函数（handlePreview）

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message="加载失败" description="无法加载图片列表，请检查网络或稍后重试。" className="mb-8" />;

  // 文件类型色值映射
  const typeColorMap: Record<string, string> = {
    PNG: 'blue',
    JPG: 'green',
    JPEG: 'green',
    WEBP: 'purple',
    GIF: 'orange',
  };

  const columns: ColumnsType<ProductImage> = [
    {
      title: '图片预览',
      key: 'preview',
      width: 120,
      align: 'center' as const,
      render: (_, record) => (
        <div className="flex items-center justify-center">
          <Image
            src={record.file_url}
            alt={record.filename}
            width={72}
            height={72}
            // ✅ 关键修改 1：禁用预览功能
            preview={false}
            // ✅ 关键修改 2：禁用右键菜单
            onContextMenu={(e) => {
              e.preventDefault();
              return false;
            }}
            fallback="image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhESMIAAAAABJRU5ErkJggg=="
            className="object-cover rounded-lg border border-gray-100 shadow-sm"
            onError={() => {
              message.warning('图片加载失败，请点击"刷新列表"获取最新链接。');
            }}
          />
        </div>
      ),
    },
    {
      title: '文件名称',
      dataIndex: 'filename',
      key: 'filename',
      ellipsis: true,
      render: (text: string) => (
        <span className="font-mono text-sm text-gray-800 break-all max-w-md">{text}</span>
      ),
    },
    {
      title: '图片版本',
      dataIndex: 'version',
      key: 'version',
      width: 100,
      align: 'center' as const,
      render: (version: number) => <Tag color="blue" className="px-2">{version}</Tag>,
    },
    {
      title: '文件类型',
      dataIndex: 'content_type',
      key: 'content_type',
      width: 110,
      align: 'center' as const,
      render: (type: string) => {
        const ext = type.split('/')[1]?.toUpperCase() || type;
        const color = typeColorMap[ext] || 'gray';
        return <Tag color={color}>{ext}</Tag>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      align: 'center' as const,
      render: (createdAt: string) => {
        return new Date(createdAt).toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        });
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      align: 'center' as const,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button 
            size="small" 
            icon={<DeleteOutlined className="text-xs" />}
            danger
            onClick={() => handleDelete(record.filename)}
            loading={deleteMutation.isPending && deleteMutation.variables === record.filename}
            className="hover:shadow-sm transition-shadow"
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="w-full space-y-8">
      {/* 标题 + 操作区 */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <PictureOutlined className="text-blue-500" />
            产品图片管理
          </h1>
          {product && (
            <p className="text-gray-600 mt-1 flex items-center gap-2">
              所属产品：
              <Tag color="blue" className="px-3 py-1">{product.name}</Tag>
              {product.firmware_version && (
                <Tag color="purple" className="px-2 py-1">v{product.firmware_version}</Tag>
              )}
            </p>
          )}
          <p className="text-gray-500 mt-1">
            共 <span className="font-medium text-blue-600">{images.length}</span> 张图片资源
          </p>
        </div>
        <Space size="middle" wrap>
          <Button 
            onClick={handleBack}
            icon={<ArrowLeftOutlined />}
            className="hover:bg-gray-50 transition-colors"
          >
            返回产品详情
          </Button>
          <Button 
            onClick={handleRefresh} 
            icon={<ReloadOutlined />} 
            disabled={imageQuery.isFetching}
            loading={imageQuery.isFetching}
            className="hover:bg-gray-50 transition-colors"
          >
            刷新列表
          </Button>
          <Button 
            type="primary" 
            onClick={handleUpload}
            icon={<UploadOutlined />}
            className="px-6 hover:shadow-md transition-shadow"
          >
            上传图片
          </Button>
        </Space>
      </div>

      {/* 重要提示 */}
      <Alert
        message="重要操作提示"
        description={
          <div className="space-y-1 text-gray-700">
            <p>• <strong className="text-orange-600">上传同名文件会覆盖旧版本</strong>（版本号自动递增），旧文件将被永久删除</p>
            <p>• 覆盖上传后系统会自动刷新列表，立即显示最新图片数据。</p>
            <p>• 如遇图片"加载失败"，请点击「刷新列表」获取最新访问链接。</p>
            <p>• <strong className="text-red-600">删除操作不可恢复</strong>，请确认无业务关联后再操作。</p>
          </div>
        }
        type="warning"
        showIcon
        className="rounded-lg shadow-sm"
        banner
      />

      <br />

      {/* 图片列表卡片 */}
      <Card 
        className="border-0 shadow-sm hover:shadow-md transition-shadow duration-300"
        style={{ padding: '0px' } as React.CSSProperties}
      >
        {images.length === 0 ? (
          <div className="text-center py-16 flex flex-col items-center justify-center">
            <div className="text-8xl text-gray-200 mb-6">🖼️</div>
            <p className="text-gray-500 text-lg mb-2">暂无图片资源</p>
            <p className="text-gray-400 text-sm mb-8">上传后的图片将在此处展示，支持预览、下载和删除。</p>
            <Button 
              type="primary" 
              icon={<UploadOutlined />} 
              size="large" 
              onClick={handleUpload}
              className="px-8 py-2 hover:shadow-md transition-shadow"
            >
              上传第一张图片
            </Button>
          </div>
        ) : (
          <Table
            key={`table-${pagination.pageSize}-${pagination.current}`}
            columns={columns}
            dataSource={isRefreshing ? [] : images}
            rowKey="id"
            loading={imageQuery.isFetching}
            pagination={{
              ...pagination,
              pageSize: pagination.pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (total) => `共 ${total} 张图片`,
              className: 'py-4',
              onChange: (page, pageSize) => {
                setPagination({
                  current: page,
                  pageSize: pageSize || pagination.pageSize,
                });
              },
            }}
            bordered={false}
            size="middle"
            scroll={{ x: 'max-content' }}
            style={{
              '--ant-table-row-hover-bg': '#f5faff',
              '--ant-table-header-text-color': '#667085',
              '--ant-table-border-color': '#e8e8e8'
            } as React.CSSProperties}
          />
        )}
      </Card>
    </div>
  );
};